import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  styled,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface UseCaseSelectorCardProps {
  id: string;
  title: string;
  imageSrc: string;
  options: string[];
  defaultValue?: string;
  onChange?: (value: string) => void;
}

const SelectorCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.2s ease',
  textAlign: 'center',
  minHeight: '200px',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
  },
}));

const SelectorContent = styled(CardContent)(() => ({
  padding: '1.5rem 1rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
  flex: 1,
  '&:last-child': {
    paddingBottom: '1.5rem',
  },
}));

const SelectorImage = styled('img')(() => ({
  width: '80px',
  height: '80px',
  objectFit: 'contain',
  marginBottom: '0.5rem',
}));

const SelectorTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '1rem',
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  maxWidth: '140px',
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.primary.main,
  '& .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '8px',
  },
  '& .MuiSelect-select': {
    padding: '8px 12px',
    textAlign: 'center',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: '2px',
  },
}));

const MobileUseCaseSelectorCard: React.FC<UseCaseSelectorCardProps> = ({
  id,
  title,
  imageSrc,
  options,
  defaultValue,
  onChange,
}) => {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState(defaultValue || options[0] || '');

  const handleChange = (value: string) => {
    setSelectedValue(value);
    if (onChange) {
      onChange(value);
    }
    
    // LocalStorage speichern
    localStorage.setItem(`usecase-${id}`, value);
  };

  return (
    <SelectorCard>
      <SelectorContent>
        <SelectorImage src={imageSrc} alt={title} />
        <SelectorTitle>{title}</SelectorTitle>
        
        <StyledFormControl variant="outlined">
          <StyledSelect
            value={selectedValue}
            onChange={(e) => handleChange(e.target.value as string)}
            IconComponent={KeyboardArrowDownIcon}
            displayEmpty
          >
            {options.map((option) => (
              <MenuItem 
                key={option} 
                value={option}
                sx={{ 
                  fontSize: '0.875rem',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(161, 183, 165, 0.1)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(161, 183, 165, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(161, 183, 165, 0.3)',
                    },
                  },
                }}
              >
                {option}
              </MenuItem>
            ))}
          </StyledSelect>
        </StyledFormControl>
      </SelectorContent>
    </SelectorCard>
  );
};

export default MobileUseCaseSelectorCard;